import React from "react";
import THEME from "../theme";
import { GlowDot, Badge } from "./UIComponents";
import { useGeodataQuery } from "../hooks/useGeodataQuery";
import { VERIFICATION_REFERENCE_LINKS } from "../config/dataSources";

function fmtTemp(v) {
  if (v == null || Number.isNaN(v)) return "—";
  return `${Number(v).toFixed(1)}°C`;
}

function SourceLink({ href, label }) {
  if (!href) return null;
  return (
    <a href={href} target="_blank" rel="noopener noreferrer" className="mt-1 inline-block text-[10px] font-medium text-accent hover:underline">
      Source: {label || href}
    </a>
  );
}

function DataBadge({ children, variant = "neutral" }) {
  const cls =
    variant === "proxy"
      ? "border-accent/40 bg-accent-muted text-accent"
      : variant === "fallback"
        ? "border-warning/40 bg-warning/10 text-warning"
        : "border-border bg-canvas-muted text-fg-secondary";
  return (
    <span className={`rounded-full border px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wide ${cls}`} role="status">
      {children}
    </span>
  );
}

export default function LiveDataPanel({ lat, lng, siteName, pollMinutes = 15 }) {
  const { data: envelope, isFetching, isError, error, refetch, dataUpdatedAt } = useGeodataQuery(lat, lng, pollMinutes);

  const payload = envelope?.data;
  const loading = isFetching && !payload;
  const marine = payload?.open_meteo_marine;
  const forecast = payload?.open_meteo_forecast;
  const owm = payload?.open_weather;
  const canada = payload?.canada_open_dfo;
  const nldi = payload?.usgs_nldi;
  const epa = payload?.epa_datagov;
  const obis = payload?.obis_occurrence;
  const refs = payload?.references?.length ? payload.references : VERIFICATION_REFERENCE_LINKS;

  const lastIso = envelope?.lastOkAt
    ? new Date(envelope.lastOkAt).toISOString()
    : dataUpdatedAt
      ? new Date(dataUpdatedAt).toISOString()
      : null;

  const errMsg = envelope?.err || (isError && error?.message) || "";
  const showDegraded = !!payload?._fatalFallback || !!marine?._mock || !!forecast?._mock;

  return (
    <div
      className="rounded-ds-xl border border-border bg-surface p-4 shadow-card transition-shadow duration-base hover:shadow-card-hover"
      role="region"
      aria-label="Live geodata for selected coordinates"
    >
      <div className="mb-3 flex flex-wrap items-center justify-between gap-2">
        <div className="text-xs font-semibold uppercase tracking-wide text-accent">
          Live geodata — {siteName || `${lat.toFixed(2)}, ${lng.toFixed(2)}`}
        </div>
        <div className="flex flex-wrap items-center gap-2">
          {lastIso && (
            <span className="rounded-full border border-border bg-canvas-subtle px-2 py-0.5 text-[10px] font-medium text-fg-secondary" title="Last successful refresh (ISO 8601)">
              Last updated: {lastIso}
            </span>
          )}
          <button
            type="button"
            onClick={() => refetch()}
            disabled={isFetching}
            className="rounded-ds border border-border bg-canvas-subtle px-2.5 py-1 text-xs font-medium text-fg-secondary transition-colors duration-fast hover:bg-canvas-muted focus:outline-none focus-visible:shadow-focus disabled:cursor-wait"
          >
            Refresh
          </button>
        </div>
      </div>
      {envelope?.fromCache && errMsg && (
        <div className="mb-2 rounded-ds border border-warning/30 bg-warning/10 px-3 py-2 text-xs text-fg" role="status">
          {errMsg}
        </div>
      )}
      {!envelope?.fromCache && errMsg && !payload && (
        <div className="mb-2 text-xs text-danger" role="alert">
          {errMsg}
        </div>
      )}
      {showDegraded && payload && (
        <div className="mb-2 rounded-ds border border-warning/30 bg-warning/10 px-3 py-2 text-xs text-fg" role="status">
          Failed (CORS/Network). Using fallback/cached where needed. Check each block for its official source link.
        </div>
      )}
      {loading && <div className="text-sm text-fg-tertiary">Loading…</div>}
      {isError && !payload && <div className="text-sm text-danger">Failed (CORS/Network). {error?.message || "Unknown error"}</div>}
      {payload && (
        <div className="flex flex-col gap-3 text-xs">
          <div className="rounded-ds border border-border bg-canvas-subtle p-3">
            <div className="mb-1 flex flex-wrap items-center gap-2">
              <span className="font-semibold text-fg">Open-Meteo (marine)</span>
              <Badge color={marine?._mock ? THEME.warning : THEME.success}>{marine?._mock ? "Fallback" : "Live"}</Badge>
              {marine?._mock && <DataBadge variant="fallback">Screening SST</DataBadge>}
            </div>
            {marine?.error ? (
              <span className="text-danger">{marine.error}</span>
            ) : (
              <>
                <div className="text-fg-secondary">
                  SST: {fmtTemp(marine?.current?.sea_surface_temperature)} · Significant wave:{" "}
                  {marine?.current?.wave_height != null ? `${marine.current.wave_height} m` : "—"}
                  {marine?.current?.wave_period != null ? ` · Period ${Number(marine.current.wave_period).toFixed(1)} s` : ""}
                  {marine?.current?.wave_direction != null ? ` · Dir ${Math.round(marine.current.wave_direction)}°` : ""}
                </div>
                <div className="mt-1 text-fg-secondary">
                  Wind waves: {marine?.current?.wind_wave_height != null ? `${marine.current.wind_wave_height} m` : "—"} · Swell:{" "}
                  {marine?.current?.swell_wave_height != null ? `${marine.current.swell_wave_height} m` : "—"} · Surface current:{" "}
                  {marine?.current?.ocean_current_velocity != null ? `${marine.current.ocean_current_velocity}` : "—"}
                  {marine?.timezone ? <span className="text-fg-tertiary"> · {marine.timezone}</span> : null}
                </div>
                {marine?.hourly_preview?.length > 0 && (
                  <div className="mt-3 overflow-x-auto rounded-ds border border-border bg-surface">
                    <table className="w-full min-w-[300px] text-left text-[10px] text-fg-secondary">
                      <caption className="sr-only">Marine hourly preview</caption>
                      <thead>
                        <tr className="border-b border-border bg-canvas-subtle text-fg-tertiary">
                          <th className="px-2 py-1.5 font-semibold">Time</th>
                          <th className="px-2 py-1.5 font-semibold">Wave m</th>
                          <th className="px-2 py-1.5 font-semibold">Swell m</th>
                          <th className="px-2 py-1.5 font-semibold">SST °C</th>
                        </tr>
                      </thead>
                      <tbody>
                        {marine.hourly_preview.map((row, idx) => (
                          <tr key={idx} className="border-b border-border last:border-0">
                            <td className="whitespace-nowrap px-2 py-1 font-mono text-fg">
                              {row.time ? new Date(row.time).toLocaleString(undefined, { month: "short", day: "numeric", hour: "numeric" }) : "—"}
                            </td>
                            <td className="px-2 py-1">{row.wave_height != null ? Number(row.wave_height).toFixed(2) : "—"}</td>
                            <td className="px-2 py-1">{row.swell_wave_height != null ? Number(row.swell_wave_height).toFixed(2) : "—"}</td>
                            <td className="px-2 py-1">{row.sea_surface_temperature != null ? Number(row.sea_surface_temperature).toFixed(1) : "—"}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </>
            )}
            {marine?.note && <p className="mt-1 text-[10px] text-fg-tertiary">{marine.note}</p>}
            <SourceLink href={marine?.source} label="Open-Meteo Marine Weather API" />
          </div>
          <div className="rounded-ds border border-border bg-canvas-subtle p-3">
            <div className="mb-1 flex flex-wrap items-center gap-2">
              <span className="font-semibold text-fg">Open-Meteo (atmosphere)</span>
              {forecast?._mock && <DataBadge variant="fallback">Fallback</DataBadge>}
            </div>
            {forecast?.error ? (
              <span className="text-danger">{forecast.error}</span>
            ) : (
              <>
                <div className="text-fg-secondary">
                  Air: {fmtTemp(forecast?.current?.temperature_2m)} · RH:{" "}
                  {forecast?.current?.relative_humidity_2m != null ? `${Math.round(forecast.current.relative_humidity_2m)}%` : "—"} · Precip:{" "}
                  {forecast?.current?.precipitation != null ? `${forecast.current.precipitation} mm` : "—"} · Cloud:{" "}
                  {forecast?.current?.cloud_cover != null ? `${Math.round(forecast.current.cloud_cover)}%` : "—"}
                </div>
                <div className="mt-1 text-fg-secondary">
                  Wind: {forecast?.current?.wind_speed_10m != null ? `${forecast.current.wind_speed_10m} m/s` : "—"}
                  {forecast?.current?.wind_direction_10m != null ? ` @ ${Math.round(forecast.current.wind_direction_10m)}°` : ""}
                  {forecast?.timezone ? <span className="text-fg-tertiary"> · {forecast.timezone}</span> : null}
                </div>
                {forecast?.hourly_preview?.length > 0 && (
                  <div className="mt-3 overflow-x-auto rounded-ds border border-border bg-surface">
                    <table className="w-full min-w-[320px] text-left text-[10px] text-fg-secondary">
                      <caption className="sr-only">Hourly forecast preview</caption>
                      <thead>
                        <tr className="border-b border-border bg-canvas-subtle text-fg-tertiary">
                          <th className="px-2 py-1.5 font-semibold">Time (local)</th>
                          <th className="px-2 py-1.5 font-semibold">°C</th>
                          <th className="px-2 py-1.5 font-semibold">mm</th>
                          <th className="px-2 py-1.5 font-semibold">m/s</th>
                          <th className="px-2 py-1.5 font-semibold">Cloud %</th>
                        </tr>
                      </thead>
                      <tbody>
                        {forecast.hourly_preview.map((row, idx) => (
                          <tr key={idx} className="border-b border-border last:border-0">
                            <td className="whitespace-nowrap px-2 py-1 font-mono text-fg">
                              {row.time ? new Date(row.time).toLocaleString(undefined, { month: "short", day: "numeric", hour: "numeric" }) : "—"}
                            </td>
                            <td className="px-2 py-1">{row.temperature_2m != null ? Number(row.temperature_2m).toFixed(1) : "—"}</td>
                            <td className="px-2 py-1">{row.precipitation != null ? Number(row.precipitation).toFixed(2) : "—"}</td>
                            <td className="px-2 py-1">{row.wind_speed_10m != null ? Number(row.wind_speed_10m).toFixed(1) : "—"}</td>
                            <td className="px-2 py-1">{row.cloud_cover != null ? Math.round(row.cloud_cover) : "—"}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </>
            )}
            <SourceLink href={forecast?.source} label="Open-Meteo forecast API" />
          </div>
          {obis && (
            <div className="rounded-ds border border-border bg-canvas-subtle p-3">
              <div className="mb-1 flex flex-wrap items-center gap-2">
                <span className="font-semibold text-fg">OBIS (biodiversity)</span>
                {obis.via_proxy && <DataBadge variant="proxy">Proxy</DataBadge>}
                {obis.fallback && <DataBadge variant="fallback">Estimate</DataBadge>}
              </div>
              {obis.error ? (
                <span className="text-danger">{obis.error}</span>
              ) : (
                <div className="text-fg-secondary">
                  <div>
                    <span>Occurrence records (API total): </span>
                    <strong className="text-fg">{obis.total ?? obis.display_total ?? "—"}</strong>
                    {obis.fallback && obis.total == null ? <span className="ml-1 text-fg-tertiary">(screening)</span> : null}
                  </div>
                  {obis.geometry ? <div className="mt-1 text-[10px] text-fg-tertiary">Geometry: {obis.geometry}</div> : null}
                </div>
              )}
              <SourceLink href={obis.source || "https://api.obis.org/v3/occurrence"} label="OBIS API v3 occurrence" />
            </div>
          )}
          {owm && (
            <div className="rounded-ds border border-border bg-canvas-subtle p-3">
              <div className="mb-1 font-semibold text-fg">OpenWeatherMap</div>
              {owm.error ? <span className="text-fg-tertiary">Set OPENWEATHER_API_KEY on the backend.</span> : (
                <div className="text-fg-secondary">
                  {owm.name}: {fmtTemp(owm.main?.temp)} · {owm.weather?.[0]?.description || ""}
                </div>
              )}
              {owm.source && <SourceLink href={owm.source} label="OpenWeatherMap API" />}
            </div>
          )}
          <div className="rounded-ds border border-border bg-canvas-subtle p-3">
            <div className="mb-1 flex flex-wrap items-center gap-2">
              <span className="font-semibold text-fg">Canada Open Data (DFO)</span>
              {canada?.static_cached && <DataBadge variant="neutral">Cached</DataBadge>}
              {canada?.via_proxy && !canada?.static_cached && <DataBadge variant="proxy">Proxy</DataBadge>}
              {canada?.fallback && !canada?.static_cached && <DataBadge variant="fallback">Portal only</DataBadge>}
            </div>
            {canada?.cache_message && <p className="mb-2 text-xs font-medium text-success">{canada.cache_message}</p>}
            {canada?.browser_only && canada?.note ? (
              <p className="text-xs leading-relaxed text-fg-secondary">{canada.note}</p>
            ) : canada?.error && !canada?.packages?.length ? (
              <span className="text-danger">{canada.error}</span>
            ) : (
              <ul className="m-0 list-disc space-y-1 pl-4 text-fg-secondary">
                {(canada?.packages || []).slice(0, 5).map((p) => (
                  <li key={p.name || p.title}>
                    <a href={p.url} target="_blank" rel="noopener noreferrer" className="text-accent hover:text-accent-hover underline-offset-2 hover:underline">
                      {p.title}
                    </a>
                  </li>
                ))}
              </ul>
            )}
            {canada?.count != null && <p className="mt-1 text-[10px] text-fg-tertiary">Indexed packages (cached): {canada.count}</p>}
            {canada?.note && !canada?.browser_only && !canada?.static_cached && <p className="mt-1 text-[10px] text-fg-tertiary">{canada.note}</p>}
            {canada?.source && <SourceLink href={canada.source} label="Open Government (DFO) search" />}
          </div>
          <div className="rounded-ds border border-border bg-canvas-subtle p-3">
            <div className="mb-1 flex flex-wrap items-center gap-2">
              <span className="font-semibold text-fg">USGS NLDI (hydrography links)</span>
              {nldi?.static_cached && <DataBadge variant="neutral">Cached</DataBadge>}
              {nldi?.via_proxy && !nldi?.static_cached && <DataBadge variant="proxy">Proxy</DataBadge>}
              {nldi?.fallback && !nldi?.static_cached && <DataBadge variant="fallback">Fallback</DataBadge>}
            </div>
            {nldi?.cache_message && <p className="mb-2 text-xs font-medium text-success">{nldi.cache_message}</p>}
            {nldi?.features?.length ? (
              <ul className="m-0 list-disc space-y-1 pl-4 text-fg-secondary">
                {nldi.features.map((f, i) => (
                  <li key={i}>
                    {typeof f === "object" ? (
                      <>
                        {f.name}
                        {f.distance ? <span className="text-fg-tertiary"> — {f.distance}</span> : null}
                      </>
                    ) : (
                      f
                    )}
                  </li>
                ))}
              </ul>
            ) : (nldi?.browser_only || nldi?.fallback) && nldi?.note ? (
              <div className="text-xs leading-relaxed text-fg-secondary">
                <p className="mb-2">{nldi.note}</p>
                {nldi.try_url && (
                  <a href={nldi.try_url} target="_blank" rel="noopener noreferrer" className="font-medium text-accent hover:underline">
                    Open NLDI query in new tab
                  </a>
                )}
              </div>
            ) : nldi?.error ? (
              <span className="text-danger">{nldi.error}</span>
            ) : (
              <div className="text-fg-secondary">Features near point: {nldi?.feature_count ?? 0}</div>
            )}
            {nldi?.source && <SourceLink href={nldi.source} label="USGS National Hydrography" />}
          </div>
          <div className="rounded-ds border border-border bg-canvas-subtle p-3">
            <div className="mb-1 font-semibold text-fg">US EPA portals</div>
            <p className="mb-2 text-fg-secondary">{epa?.note}</p>
            <div className="flex flex-wrap gap-2">
              {(epa?.links || []).map((l) => (
                <a
                  key={l.url}
                  href={l.url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="rounded-ds border border-border bg-surface px-2 py-1 text-[11px] text-accent hover:bg-canvas-subtle"
                >
                  {l.title}
                </a>
              ))}
            </div>
          </div>
          <p className="text-[10px] text-fg-tertiary">Bundle time: {payload.fetched_at}</p>
        </div>
      )}
      <div className="mt-3 border-t border-border pt-3">
        <div className="mb-2 text-[10px] font-semibold uppercase tracking-wide text-fg-tertiary">Verification links</div>
        <ul className="m-0 grid gap-1 sm:grid-cols-2">
          {refs.map((r) => (
            <li key={r.url}>
              <a href={r.url} target="_blank" rel="noopener noreferrer" className="text-[10px] text-accent hover:underline">
                {r.name}
              </a>
            </li>
          ))}
        </ul>
      </div>
      <div className="mt-3 flex items-center gap-2 text-[10px] text-fg-tertiary">
        <GlowDot color={THEME.success} size={5} pulse={!!payload && !errMsg && envelope?.liveOk !== false} />
        <span>
          TanStack Query polls every {Math.min(Math.max(pollMinutes, 5), 15)} min after a successful live fetch; cache TTL 1h in localStorage.
        </span>
      </div>
    </div>
  );
}
